/**
 * Which search filters each access tier may use.
 *
 * Two jobs, and the second is the one that matters: the sidebar greys out
 * filters a visitor cannot use, and the server **removes them from the query**
 * regardless of what the client sent. A UI-only gate is bypassed by editing
 * the URL, which is precisely what bulk extraction does — enumerating WZ codes
 * against a server-rendered page needs no JavaScript at all.
 *
 * Enforcement happens on the backend request shape rather than on the sidebar's
 * `FilterParams`, because that is where both entry points converge:
 *
 *   * `/search` — `parseSearchURLParams` → `buildBackendFilters` (SSR), and
 *     `/api/search/companies` for every subsequent client search;
 *   * `/listen/[slug]` — `filterParamsToParsed` → `buildBackendFilters`.
 *
 * `/listen` pages are deliberately **not** gated. Their filters come from
 * `seo-lists.config.ts`, not from the caller: they are a fixed, public,
 * already-indexed result set, so they are not an extraction vector. The API
 * route re-applies a list's own preset by slug for the same reason — a caller
 * can only ever reproduce a page we already publish.
 */

import type { Session } from '@/lib/permissions';
import { isAdmin, isPro } from '@/lib/permissions';

export type FilterTier = 'public' | 'user' | 'pro';

/**
 * Keys on the backend request that each tier unlocks. A tier also grants
 * everything below it.
 *
 * `revenue` carries `includeNull` with it, so the modifier follows the filter
 * it modifies rather than needing its own entry.
 */
export const GATED_BACKEND_KEYS: Record<'user' | 'pro', readonly string[]> = {
  user: [
    'industries',
    'tags',
    'aktiva',
    'profit',
    'employeeCount',
    'personName',
    'avgBirthYear',
    'city',
    'Bundesland',
    'financialYear',
    'hasYearlyMetadata',
  ],
  pro: [
    'naceSectors',
    'naceSubsectors',
    'cpvCode',
    'revenue',
    'ustIdnr',
    'memberships',
    'legalStatus',
    'tenderRole',
    // Derived from the register's board history rather than read off it,
    // and the enumerating use is the whole value: "family businesses in
    // Bavaria whose founder is over 65" is a prospecting list, not a
    // lookup. Reading one company's profile stays free on its detail page.
    'familyManaged',
    'founderLed',
    'successionRisk',
  ],
};

/**
 * Tier required by each control in the sidebar, keyed by its `FilterParams`
 * field. Drives the lock badges; `GATED_BACKEND_KEYS` drives enforcement.
 * The two are separate because one control can map to several request keys
 * (the WZ picker sends both `naceSectors` and `naceSubsectors`).
 */
export const CONTROL_TIER: Record<string, 'user' | 'pro'> = {
  industries: 'user',
  tags: 'user',
  aktivaMin: 'user',
  aktivaMax: 'user',
  profitMin: 'user',
  profitMax: 'user',
  employeesMin: 'user',
  employeesMax: 'user',
  personName: 'user',
  ageRanges: 'user',
  city: 'user',
  Bundesland: 'user',
  financialYear: 'user',
  hasYearlyMetadata: 'user',

  naceCodes: 'pro',
  cpvCode: 'pro',
  revenueMin: 'pro',
  revenueMax: 'pro',
  ustIdnr: 'pro',
  memberships: 'pro',
  legalStatus: 'pro',
  tenderRole: 'pro',
  // `companySize` is deliberately absent, which `controlLock` reads as free.
  // Legal status and tender role add a new axis to enumerate the register by;
  // this one repairs an existing gap — the revenue filter reaches ~70k
  // companies because most of the register publishes no turnover at all, and
  // the size class is what makes the other ~761k findable. Charging for that
  // would be charging to fix a hole.
  familyManaged: 'pro',
  founderLed: 'pro',
  successionRisk: 'pro',
};

/**
 * Every control each sidebar group holds, gated or not.
 *
 * Exists so a group whose controls are *all* out of reach can say so on its
 * header, instead of making the visitor open it and read five chips to work
 * out that nothing inside is available. The group still opens — seeing which
 * filters exist is the reason to sign up.
 *
 * Controls absent from `CONTROL_TIER` are open to everyone and therefore
 * leave their group unmarked; listing them here is what makes that true by
 * construction rather than by remembering to.
 */
export const GROUP_CONTROLS: Record<string, readonly string[]> = {
  profile: [
    'rechtsformCodes',
    'foundedAt',
    'memberships',
    // Both are pro. Reading either fact about *one* company stays free on
    // its detail page — what pro buys is the ability to enumerate the
    // register by it, which is the extraction-shaped use.
    'legalStatus',
    'tenderRole',
    'hasWebsite',
    'hasEmail',
  ],
  location: ['city', 'Bundesland'],
  industry: ['industries', 'naceCodes', 'tags', 'cpvCode'],
  size: [
    'revenueMin',
    'revenueMax',
    'aktivaMin',
    'aktivaMax',
    'profitMin',
    'profitMax',
    'employeesMin',
    'employeesMax',
    'financialYear',
    'hasYearlyMetadata',
  ],
  people: ['personName', 'ageRanges', 'familyManaged', 'founderLed', 'successionRisk'],
  register: [
    'euid',
    'registerId',
    'registerCourt',
    'registerType',
    'leiCode',
    'ustIdnr',
  ],
};

/** Admins count as pro. */
export function tierForSession(session: Session | null): FilterTier {
  if (!session?.user) return 'public';
  return isAdmin(session) || isPro(session) ? 'pro' : 'user';
}

/** True when `tier` is allowed to use a control requiring `required`. */
export function tierAllows(tier: FilterTier, required: 'user' | 'pro'): boolean {
  if (required === 'user') return tier === 'user' || tier === 'pro';
  return tier === 'pro';
}

/** The tier a single control needs, or null when this visitor may use it. */
export function controlLock(
  control: string,
  tier: FilterTier,
): 'user' | 'pro' | null {
  const required = CONTROL_TIER[control];
  if (!required) return null;
  return tierAllows(tier, required) ? null : required;
}

/**
 * The lock chip to show on a whole group's header, or null when at least one
 * of its controls is usable.
 *
 * One usable control is enough to leave the header unmarked, because the
 * chip means "nothing in here is available to you" — a group you can partly
 * use is described by its own per-control chips, not by its header.
 *
 * When several tiers are represented, the *cheapest* one wins: a guest
 * looking at Size & financials can unlock five of its six controls by
 * signing in, so "Sign in" is the honest call to action even though revenue
 * needs Pro on top of it.
 */
export function groupLock(
  groupId: string,
  tier: FilterTier,
): 'user' | 'pro' | null {
  const controls = GROUP_CONTROLS[groupId];
  if (!controls || controls.length === 0) return null;

  let cheapest: 'user' | 'pro' | null = null;
  for (const control of controls) {
    const lock = controlLock(control, tier);
    if (lock === null) return null; // something here is usable — stay open
    if (lock === 'user') cheapest = 'user';
    else if (cheapest === null) cheapest = 'pro';
  }
  return cheapest;
}

/**
 * A filter key only counts as *sent* when it carries a value.
 *
 * `buildBackendFilters` always emits some keys (`Bundesland: []`), so an
 * identity check alone would report a filter the caller never set as
 * "stripped" — which the UI then explains to the user and the analytics
 * then records.
 */
function isSet(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.length > 0;
  return true;
}

/**
 * Remove every filter the tier may not use, returning the keys dropped.
 *
 * Deletes rather than nulls the keys: the backend treats an explicit `null`
 * for some fields as a filter in its own right, and a stripped query must be
 * indistinguishable from one that never asked.
 */
export function stripGatedBackendFilters<T extends object>(
  params: T,
  tier: FilterTier,
): { params: T; removed: string[] } {
  if (tier === 'pro') return { params, removed: [] };

  const forbidden = [
    ...GATED_BACKEND_KEYS.pro,
    ...(tier === 'public' ? GATED_BACKEND_KEYS.user : []),
  ];

  const out = { ...params } as Record<string, unknown>;
  const removed: string[] = [];
  for (const key of forbidden) {
    if (!isSet(out[key])) continue;
    delete out[key];
    removed.push(key);
  }
  return { params: out as T, removed };
}

/** Shared with the API route so a `/listen` preset is spliced back in on
 *  the same "does it carry a value" rule the stripping used. */
export { isSet as isFilterSet };
